import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/Button/Button';
import useViewport from '@/hooks/useViewport';
import { classNameMerge } from '@/lib/utils';
import GET_CATEGORIES from '@/queries/getCategories.gql';
import { useLazyQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import CategoryButton from './components/CategoryButton/CategoryButton';
import MovementButton from './components/MovementButton/MovementButton';
export default function MovementButtons(_a) {
    var availableMovements = _a.availableMovements, movement = _a.movement, onMovementChange = _a.onMovementChange;
    var width = useViewport().width;
    var _b = useState(true), isLoading = _b[0], setIsLoading = _b[1];
    var _c = useState(null), activeMovement = _c[0], setActiveMovement = _c[1];
    var _d = useState([]), activeCategories = _d[0], setActiveCategories = _d[1];
    var _e = useState(false), showCategories = _e[0], setShowCategories = _e[1];
    var _f = useLazyQuery(GET_CATEGORIES), getCategories = _f[0], loadingCategories = _f[1].loading;
    var excludedCategories = ['animals', 'first-nations'];
    useEffect(function () {
        if (availableMovements.length > 0 && movement) {
            var movementIndex = availableMovements.findIndex(function (m) { return m.slug === movement.slug; });
            var activeMovementIndex = movementIndex >= 0 ? movementIndex : 0;
            setActiveMovement(availableMovements[activeMovementIndex]);
        }
        setIsLoading(availableMovements.length === 0);
        getCategories({
            onCompleted: function (categoryResponse) {
                setActiveCategories(categoryResponse.categories
                    .filter(function (category) { return category.display; })
                    .filter(function (category) { return !excludedCategories.includes(category.slug); })
                    .sort(function (a, b) { return a.sortOrder - b.sortOrder; }));
            },
        });
    }, [movement, availableMovements]);
    var distributedRows = useMemo(function () {
        if (!availableMovements.length)
            return [];
        // Determine max columns based on viewport width
        var maxColumns = 1; // tiny screen default
        if (width >= 1152) {
            maxColumns = 6; // desktop
        }
        else if (width >= 840) {
            maxColumns = 4; // larger tablet
        }
        else if (width >= 550) {
            maxColumns = 3; // mid-sized tablet
        }
        else if (width >= 340) {
            maxColumns = 2; // mobile
        }
        var total = availableMovements.length;
        var remaining = total;
        var startIndex = 0;
        var rowsCount = Math.ceil(total / maxColumns);
        var rows = Array.from({ length: rowsCount }).map(function () {
            var rowsLeft = Math.ceil(remaining / maxColumns);
            var base = Math.floor(remaining / rowsLeft);
            var remainder = remaining % rowsLeft;
            var itemsThisRowCount = base + (remainder > 0 ? 1 : 0);
            var rowItems = availableMovements.slice(startIndex, startIndex + itemsThisRowCount);
            startIndex += itemsThisRowCount;
            remaining -= itemsThisRowCount;
            return rowItems;
        });
        return rows;
    }, [availableMovements, width]);
    var maxRowCount = useMemo(function () {
        if (!distributedRows.length)
            return 0;
        return Math.max.apply(Math, distributedRows.map(function (row) { return row.length; }));
    }, [distributedRows]);
    return (_jsx("section", { "aria-busy": isLoading, "aria-label": "Select a movement to support", className: "w-full flex justify-center max-xxs:pt-[1rem] p-[0.5rem] xxs:p-[1rem] md:p-[2rem] pb-8", "data-testid": "movement-button-section", id: "movement-button-section", role: "region", children: _jsxs("div", { className: "w-full max-w-7xl mx-auto", children: [_jsx("div", { className: "flex gap-2 sm:gap-4 items-end mb-[1.5rem]", children: _jsx("h2", { className: "section-title", id: "campaigns-section-title", children: "Featured Movements" }) }), isLoading ? (_jsx("div", { "aria-label": "Loading movements", className: classNameMerge("\n                flex\n                flex-wrap\n                justify-center\n                gap-2\n                mx-auto\n                max-w-[430px]\n                xs:max-w-[640px]\n                lgx:max-w-[1280px]\n                "), children: Array.from({ length: 6 }).map(function (_, index) { return (_jsx(Skeleton, { "aria-busy": "true", containerTestId: "campaign-skeleton", height: 120 }, "movement_skel_".concat(index))); }) })) : (_jsxs("div", { "aria-label": "All movements", className: "gap-2 w-full flex flex-col items-center", children: [distributedRows.map(function (rowItems, rowIndex) {
                            return (_jsx("div", { className: "flex justify-center gap-2 flex-wrap w-full", "data-testid": "movement-button-container", children: rowItems.map(function (movement, itemIndex) { return (_jsx(MovementButton, { isActive: activeMovement === movement, maxRowCount: maxRowCount, movement: movement, onClick: function () { return onMovementChange(movement); } }, "movement-button-".concat(movement.slug, "-").concat(rowIndex, "-").concat(itemIndex))); }) }, rowIndex));
                        }), _jsx("div", { className: "flex justify-center items-center gap-4 mt-[1.5rem] flex-wrap", children: showCategories &&
                                activeCategories.map(function (cat) { return (_jsx(CategoryButton, { href: "/discover?categories[]=".concat(cat.title.toLowerCase()), title: cat.title }, "category-button-".concat(cat.slug))); }) }), !loadingCategories && activeCategories.length > 0 && (_jsx("div", { className: "flex justify-center lgx:col-span-6 sm:col-span-3 xxs:col-span-2 3xs:col-span-2 col-span-1 mt-[1.5rem]", children: _jsx(Button, { className: classNameMerge("rounded-full font-bold text-sm sm:text-base"), "data-testid": "hero-primary-button", onClick: function () { return setShowCategories(!showCategories); }, size: "lg", variant: "outline", children: showCategories ? 'Show Less' : "+ ".concat(activeCategories.length, " More") }) }))] }))] }) }));
}
